import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import ShowTime from '../../utils/ShowTime/ShowTime';
import CustomLoader from '../../utils/DataTableLoader/CustomLoader';
import '../ActivityLogModal/ActivityLogModal.css';
import { hasMetadata, MetadataDetail } from './CommandMetadata';

const CommandLogModal = ({
  show,
  onHide,
  machineId,
  licenseId,
  title = 'Command Logs',
}) => {
  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [pending, setPending] = useState(false);

  const getTimeDifference = (currentTime, nextTime) => {
    if (!nextTime || !currentTime) return null;
    const diffMs = new Date(currentTime) - new Date(nextTime);
    if (diffMs <= 0) return null;

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0)    return diffDays === 1    ? '1 day later'    : `${diffDays} days later`;
    if (diffHours > 0)   return diffHours === 1   ? '1 hour later'   : `${diffHours} hours later`;
    if (diffMinutes > 0) return diffMinutes === 1 ? '1 minute later' : `${diffMinutes} minutes later`;
    if (diffSeconds > 0) return diffSeconds === 1 ? '1 second later' : `${diffSeconds} seconds later`;
    return 'Right after';
  };

  // When viewing by license, the same machine_id can change over time as
  // machines move on/off the license — so it's useful to see which one
  // produced each row. When already filtering by machine, hide the column.
  const showMachineColumn = !!licenseId && !machineId;

  const columns = [
    {
      name: 'Command',
      selector: row => row.command,
      sortable: true,
      sortField: 'command',
      minWidth: '200px',
      grow: 2,
      cell: row => (
        <div className="activity-type-cell">
          <span
            className="activity-badge"
            style={{
              backgroundColor: '#1976d220',
              color: '#1976d2',
              borderColor: '#1976d240',
              whiteSpace: 'nowrap',
            }}
          >
            {row.command}
          </span>
        </div>
      ),
    },
    ...(showMachineColumn ? [{
      name: 'Machine',
      selector: row => row.machine_id,
      sortable: true,
      sortField: 'machine_id',
      minWidth: '180px',
      maxWidth: '220px',
      cell: row => (
        <span
          title={row.machine_id || ''}
          style={{
            fontFamily: 'monospace',
            fontSize: '10px',
            backgroundColor: '#f0f0f0',
            padding: '2px 6px',
            borderRadius: '3px',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-all',
            lineHeight: '1.3',
            maxWidth: '100%',
          }}
        >
          {row.machine_id || '-'}
        </span>
      ),
    }] : []),
    {
      name: 'Timestamp',
      selector: row => row.timestamp,
      sortable: true,
      sortField: 'timestamp',
      minWidth: '220px',
      cell: (row, index) => {
        // tableData is sorted DESC by timestamp by default — the row below
        // (index + 1) is the previous command in time. Difference is from
        // the current row's timestamp back to the previous one.
        const previousTime = tableData[index + 1]?.timestamp || null;
        const timeDiff = previousTime ? getTimeDifference(row.timestamp, previousTime) : null;

        return (
          <div style={{ whiteSpace: 'pre-wrap', padding: '10px 0' }}>
            <div style={{ fontWeight: 500 }}>{row.timestamp ? ShowTime(row.timestamp) : '-'}</div>
            {timeDiff && (
              <div
                style={{
                  fontSize: '11px',
                  color: '#6c757d',
                  fontStyle: 'italic',
                  marginTop: '4px',
                  padding: '2px 8px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  display: 'inline-block',
                }}
              >
                {timeDiff} since previous
              </div>
            )}
          </div>
        );
      },
    },
    {
      name: 'Recorded',
      selector: row => row.created_at,
      sortable: true,
      sortField: 'created_at',
      minWidth: '180px',
      cell: row => (
        <div style={{ fontSize: '12px', color: '#666' }}>
          {row.created_at ? ShowTime(row.created_at) : '-'}
        </div>
      ),
    },
  ];

  const fetchCommandLogs = async () => {
    if (!machineId && !licenseId) return;
    try {
      setPending(true);

      const filterParams = {};
      let idx = 0;
      if (machineId) {
        filterParams[`filters[${idx}][column]`]   = 'machine_id';
        filterParams[`filters[${idx}][operator]`] = '=';
        filterParams[`filters[${idx}][value]`]    = machineId;
        idx++;
      }
      if (licenseId) {
        filterParams[`filters[${idx}][column]`]   = 'license_id';
        filterParams[`filters[${idx}][operator]`] = '=';
        filterParams[`filters[${idx}][value]`]    = licenseId;
        idx++;
      }

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/command_logs`, {
        params: {
          limit: pageSize,
          page,
          sort_by: sortBy,
          sort_order: sortOrder,
          ...filterParams,
        },
        headers: {
          'access-token': localStorage.getItem('accessToken'),
        },
      });

      if (response.data.status) {
        setTotalRecords(response.data.meta.pagination.total);
        setTableData(response.data.data);
      } else {
        toast.error('Failed to fetch command logs');
      }
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        console.error(error);
        toast.error('Failed to fetch command logs');
      }
    } finally {
      setPending(false);
    }
  };

  useEffect(() => {
    if (show && (machineId || licenseId)) {
      fetchCommandLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, machineId, licenseId, page, pageSize, sortBy, sortOrder]);

  const handleClose = () => {
    setTableData([]);
    setTotalRecords(0);
    setPage(1);
    onHide();
  };

  // Only surface the expander column when at least one visible row carries
  // metadata, so the table stays clean when there's nothing extra to show.
  const anyMetadata = tableData.some(hasMetadata);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      className="activity-log-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {title}
          {(machineId || licenseId) && (
            <span style={{ fontSize: '14px', fontWeight: 400, marginLeft: 12, opacity: 0.85 }}>
              {machineId && <>machine: {machineId}</>}
              {machineId && licenseId && ' · '}
              {licenseId && <>license #{licenseId}</>}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="table-wrapper">
          <DataTable
            columns={columns}
            data={tableData}
            dense
            direction="auto"
            highlightOnHover
            noHeader
            persistTableHead
            pointerOnHover
            responsive
            expandableRows={anyMetadata}
            expandableRowDisabled={row => !hasMetadata(row)}
            expandableRowsComponent={MetadataDetail}
            progressPending={pending}
            progressComponent={<CustomLoader />}
            pagination
            paginationServer
            paginationPerPage={pageSize}
            paginationTotalRows={totalRecords}
            paginationRowsPerPageOptions={[10, 20, 50, 100, 150, 500, 1000, 1500]}
            onChangePage={(p) => setPage(p)}
            onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
              setPageSize(currentRowsPerPage);
              setPage(currentPage);
            }}
            sortServer
            onSort={(column, sortDirection) => {
              setSortBy(column.sortField);
              setSortOrder(sortDirection);
            }}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CommandLogModal;
